import { get as _get } from "lodash";
import geoDist from "geo-dist";
/*
We store a location in a way that is efficient to query via firestore.

This is a custom solution! Alternatives considered:
GeoHash
Bounding box
- Firestore does not support more than one field having a range query (e.g. latitude > minLat AND longitude > minLong )

This custom approach is based on bounding box:
- The latitude is queried as normal (> AND < range queries).
- The longitude is queried in a way that enables us to use == operators!
- This is done by querying not the exact longitude value, but whether the value is in one or more value 'buckets'
- e.g. a query for longitude > 1.35 and < 1.45 will query for longitude values in buckets 1.3 and 1.4
- As you can see the longitude query is not exact (it is querying an approximate bounding box!)
- It may pull more results from the databases, and will need further client-side filtering
- To enable this to scale, multiple bucket resolutions are supported
- e.g. a longitude of 1.4567 will be in the '1', '1.4', '1.45' buckets
- Bucket values are stored as integers! So above values are 1, 14, 145
*/
export function geoEncode(longitude, latitude) {
    return {
        longitude,
        latitude,
        longitudeBuckets: {
            1: Math.floor(longitude),
            10: Math.floor(longitude * 10),
            100: Math.floor(longitude * 100)
        }
    };
}
export async function geoQuery(collectionQuery, geoPropertyPath, longitude, latitude, distance) {
    // geeky earth measurements!
    const radiusOfEarthInMiles = 3963.2;
    const circumferenceOfEarthInMiles = 2 * Math.PI * radiusOfEarthInMiles;
    // convert distance in miles to degrees
    const distanceDegrees = distance / (circumferenceOfEarthInMiles / 360);
    // 'square' bounding box
    const minLatitude = latitude - distanceDegrees;
    const maxLatitude = latitude + distanceDegrees;
    const minLongitude = longitude - distanceDegrees;
    const maxLongitude = longitude + distanceDegrees;
    // This is the easy bit - filter by latitude
    let updatedQuery = collectionQuery
        .where(geoPropertyPath + '.latitude', '>', minLatitude)
        .where(geoPropertyPath + '.latitude', '<', maxLatitude);
    // the buckets that we need to query, based on the bounding box
    const bucketValues = [];
    let bucketSize;
    let bucketName;
    const boundingBoxLengthDegrees = maxLongitude - minLongitude;
    if (boundingBoxLengthDegrees < 0.09) {
        // search using buckets with 2 decimal places
        bucketSize = 0.01;
        bucketName = geoPropertyPath + '.longitudeBuckets.100';
    }
    else if (boundingBoxLengthDegrees < 0.9) {
        // search using buckets with 1 decimal place
        bucketSize = 0.1;
        bucketName = geoPropertyPath + '.longitudeBuckets.10';
    }
    else if (boundingBoxLengthDegrees < 9) {
        // search using buckets with 0 decimal places
        bucketSize = 1.0;
        bucketName = geoPropertyPath + '.longitudeBuckets.1';
    }
    else {
        // no filter!
    }
    if (bucketName && bucketSize) {
        const startBucketValue = Math.floor(minLongitude / bucketSize);
        let bucketValue = startBucketValue;
        while (bucketValue < (maxLongitude / bucketSize)) {
            bucketValues.push(bucketValue);
            bucketValue += 1;
        }
        updatedQuery = updatedQuery.where(bucketName, 'in', bucketValues);
    }
    return await updatedQuery.get().then((snap) => {
        const allItems = snap.docs.map((doc) => ({ id: doc.id, data: doc.data() }));
        return filterByDistance(allItems, geoPropertyPath, longitude, latitude, distance);
    });
}
function filterByDistance(items, geoPropertyPath, longitude, latitude, distance) {
    console.log('all items: ' + items.length);
    const milesPerKm = 0.6213712;
    const distanceKm = distance / milesPerKm;
    const filtered = items.filter(item => {
        const geo = _get(item.data, geoPropertyPath);
        const km = geoDist(geo.latitude, geo.longitude, latitude, longitude);
        return km < distanceKm;
    });
    console.log('filtered items: ' + filtered.length);
    return filtered;
}
